// Adjuntos de cotización — contrato /api/quotes/{id}/attachments (CRM) y
// /api/quotes/public/{token}/attachments (portal público, sin JWT).

// Respuesta autenticada (CRM). downloadCount suma descargas PUBLIC + CRM.
export interface QuoteAttachmentResponse {
  id: number;
  quoteId: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: number;
  uploadedByName?: string | null;
  datetimeCreated: string;
  downloadCount: number;
  lastDownloadedAt?: string | null;
}

// Respuesta pública (portal del cliente): shape reducido, sin tracking.
export interface QuoteAttachmentPublicResponse {
  id: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export type QuoteAttachmentDownloadVia = 'PUBLIC' | 'CRM';

// GET /api/quotes/{id}/attachments/{attachmentId}/downloads — más reciente primero.
export interface QuoteAttachmentDownloadEntry {
  id: number;
  via: QuoteAttachmentDownloadVia;
  ip?: string | null;
  userAgent?: string | null;
  /** null cuando la descarga fue desde el portal público. */
  userId?: number | null;
  /** null cuando la descarga fue desde el portal público. */
  userName?: string | null;
  datetimeDownloaded: string;
}

/** Tamaño máximo permitido por el backend: 10 MB. */
export const QUOTE_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** MIME permitidos por el backend (otro MIME → 400). */
export const QUOTE_ATTACHMENT_ALLOWED_MIME_TYPES: readonly string[] = [
  // Imágenes
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  // Documentos
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  // Videos
  'video/mp4',
  'video/webm',
  'video/mpeg',
  // Comprimidos (V75)
  'application/zip',
  'application/x-zip-compressed'
];

/** Extensiones equivalentes a los MIME permitidos (respaldo cuando file.type viene vacío). */
export const QUOTE_ATTACHMENT_ALLOWED_EXTENSIONS: readonly string[] = [
  'jpg', 'jpeg', 'png', 'gif', 'webp',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt',
  'mp4', 'webm', 'mpeg', 'mpg',
  'zip'
];

/** Valor para el atributo accept del input file. */
export const QUOTE_ATTACHMENT_ACCEPT =
  QUOTE_ATTACHMENT_ALLOWED_EXTENSIONS.map(e => `.${e}`).join(',');

/**
 * Validación client-side previa a la subida (tamaño + tipo).
 * Devuelve el mensaje de error en español, o null si el archivo es válido.
 */
export function validateQuoteAttachmentFile(file: File): string | null {
  if (file.size > QUOTE_ATTACHMENT_MAX_SIZE_BYTES) {
    return `"${file.name}" supera el tamaño máximo de 10 MB.`;
  }
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mimeOk = !!file.type && QUOTE_ATTACHMENT_ALLOWED_MIME_TYPES.includes(file.type);
  const extOk = QUOTE_ATTACHMENT_ALLOWED_EXTENSIONS.includes(ext);
  if (!mimeOk && !extOk) {
    return `Tipo de archivo no permitido (.${ext || '?'}). Se aceptan imágenes, PDF, Word, Excel, PowerPoint, TXT, videos y ZIP.`;
  }
  return null;
}

export type QuoteAttachmentIconKind =
  | 'pdf' | 'image' | 'word' | 'excel' | 'ppt' | 'video' | 'text' | 'file';

/** Clasifica el adjunto por MIME (con respaldo por extensión) para elegir el ícono. */
export function quoteAttachmentIconKind(mimeType?: string | null, fileName?: string | null): QuoteAttachmentIconKind {
  const mime = (mimeType || '').toLowerCase();
  const ext = ((fileName || '').split('.').pop() || '').toLowerCase();

  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (mime.startsWith('video/') || ['mp4', 'webm', 'mpeg', 'mpg'].includes(ext)) return 'video';
  if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || ['xls', 'xlsx'].includes(ext)) return 'excel';
  if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint' || ['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc', 'docx'].includes(ext)) return 'word';
  if (mime === 'text/plain' || ext === 'txt') return 'text';
  return 'file';
}

/** Tamaño legible: 850 B / 12.4 KB / 3.2 MB. */
export function formatAttachmentSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Etiqueta legible del origen de una descarga: "Cliente (portal)" o "{nombre} (CRM)". */
export function quoteAttachmentDownloadViaLabel(entry: QuoteAttachmentDownloadEntry): string {
  if (entry.via === 'PUBLIC') return 'Cliente (portal)';
  return `${entry.userName || 'Usuario'} (CRM)`;
}
